import { Client } from "@twilio/conversations";
import { Dispatch } from "redux";
import log from "loglevel";

import { ACTION_UPDATE_CONVERSATION_STATE } from "../actionTypes";
import { reportClientError } from "../../../utils/reportClientError";
import { descreverErro } from "../../../utils/descreverErro";

/**
 * Relê o estado da conversa NO SERVIDOR e corrige o estado local.
 *
 * Por que existe (reproduzido ao vivo em 2026-08-28): com o widget sem rede, a conversa foi fechada
 * no Twilio, o evento `updated` se perdeu, e o widget continuou achando `active` — com o **composer
 * montado**. O cliente digita e leva 400. Pior: ao voltar a rede o SDK **não** se re-sincroniza
 * sozinho; reconectar restabelece o socket e deixa a tela mentindo.
 *
 * `getConversationBySid` foi VERIFICADO como fonte fresca (devolveu `closed` enquanto o store dizia
 * `active`) — apesar do "get a known conversation" da doc sugerir cache.
 *
 * Best-effort: revalidar é diagnóstico. Nunca lança — quem está tentando falar com o suporte não
 * pode ser derrubado por uma checagem nossa.
 *
 * 📦 Mora em módulo próprio (codereview, 2026-08-28): isto é sobre **estado da conversa**, não sobre
 * reconexão. Estava dentro do `reconnect.ts` só porque foi lá que a necessidade apareceu — e quem
 * chama não é só o caminho de reconexão: o `visibilitychange` do `clientListener` revalida mesmo
 * quando não há o que reconectar.
 *
 * 🔴 **Deadline e desfecho discriminado (2026-09-02).** O `await` era solto: se o SDK pendura a
 * promessa — o que acontece justamente quando a rede volta pela metade — a revalidação nunca
 * terminava, nunca corrigia a tela e não aparecia em lugar nenhum. E o retorno `void` fazia o
 * chamador supor sucesso. Agora há prazo, o desfecho diz o que houve, e a falha é contável.
 *
 * ⚠️ **Falhar aqui NÃO trava o composer, de propósito.** Não saber o estado é diferente de saber
 * que a conversa acabou. Bloquear o envio no "desconhecido" puniria o caso comum (blip de rede numa
 * conversa saudável) para proteger o raro. Na dúvida, o apostador continua podendo falar — e se a
 * conversa realmente tiver fechado, o 400 do Twilio já tem tratamento próprio.
 */

/**
 * Prazo para o GET de uma conversa.
 *
 * 📏 MEDIDO no N3 contra o Twilio real (2026-09-02, 6 amostras da REST de Conversations, que é o
 * proxy declarado — o `getConversationBySid` do SDK trafega por twilsock e não aparece como
 * requisição HTTP): 198, 206, 217, 516, 520, 564 ms. Mediana 516ms, pior caso 564ms.
 *
 * 10s são ~18x o pior caso observado. A folga é proposital: este teto existe para o SDK pendurado,
 * não para cortar rede lenta. Se for mexer, meça de novo.
 */
export const TIMEOUT_DE_REVALIDACAO_MS = 10000;

/** O chamador precisa distinguir "revalidei" de "não consegui" — devolver `void` escondia isso. */
export type DesfechoDaRevalidacao =
    | { ok: true; estado: string }
    | { ok: false; motivo: "timeout" | "erro" | "sem_estado" };

export const revalidarEstadoDaConversa = async ({
    client,
    conversationSid,
    dispatch
}: {
    client: Client;
    conversationSid: string;
    dispatch: Dispatch;
}): Promise<DesfechoDaRevalidacao> => {
    let expirou = false;
    let prazo: ReturnType<typeof setTimeout> | undefined;
    try {
        /*
         * `Promise.race` NÃO cancela o perdedor: o GET do SDK segue vivo depois do prazo. Isso é
         * seguro aqui porque, vencido o deadline, já retornamos — a resposta atrasada não tem por
         * onde escrever no store, e o estado que ela carrega estaria velho.
         */
        const conversa = await Promise.race([
            client.getConversationBySid(conversationSid),
            new Promise<never>((_, reject) => {
                prazo = setTimeout(() => {
                    expirou = true;
                    reject(new Error("timeout ao revalidar o estado da conversa"));
                }, TIMEOUT_DE_REVALIDACAO_MS);
            })
        ]);
        const estado = conversa?.state?.current;
        if (!estado) {
            reportClientError("revalidate_failed", {
                reason: "conversa devolvida sem estado",
                lookupOutcome: "unavailable"
            });
            return { ok: false, motivo: "sem_estado" };
        }
        /*
         * O SID vai junto: entre o início desta leitura e a chegada da resposta o apostador pode
         * ter começado OUTRA conversa. Sem ele o reducer não tem como saber que este estado é de
         * uma conversa que já não está na tela — e aplicá-lo encerraria um atendimento vivo.
         */
        dispatch({
            type: ACTION_UPDATE_CONVERSATION_STATE,
            payload: { conversationState: estado, conversationSid }
        });
        return { ok: true, estado };
    } catch (e) {
        const motivo = expirou ? "timeout" : "erro";
        log.warn(`revalidarConversa: não foi possível revalidar o estado da conversa: ${descreverErro(e)}`);
        /*
         * `lookupOutcome` é o campo certo: ele existe justamente para dizer "não consegui saber o
         * estado" e o backend já o revalida por allowlist de VALORES. `failureStage` é do pipeline
         * de ENVIO ("media_prepare"/"mcs_upload"/...) e não descreve uma consulta.
         */
        reportClientError("revalidate_failed", {
            reason: descreverErro(e),
            lookupOutcome: expirou ? "timeout" : "failed"
        });
        return { ok: false, motivo };
    } finally {
        if (prazo) {
            clearTimeout(prazo);
        }
    }
};
